import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, User, Scale, Ruler, Target, Dumbbell, 
  Clock, Apple, Save, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from '../hooks/use-toast';

const ProfilePage = () => {
  const { user, updateProfile, updateMetrics, updateGoals } = useAuth();
  const { language } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const [profile, setProfile] = useState({
    first_name: user?.profile?.first_name || '',
    last_name: user?.profile?.last_name || '',
    gender: user?.profile?.gender || '',
    language: user?.profile?.language || 'es'
  });

  const [metrics, setMetrics] = useState({
    height_cm: user?.profile?.metrics?.height_cm || '',
    weight_kg: user?.profile?.metrics?.weight_kg || '',
    target_weight_kg: user?.profile?.metrics?.target_weight_kg || '',
    activity_level: user?.profile?.metrics?.activity_level || 'moderate'
  });

  const [goals, setGoals] = useState({
    primary_goal: user?.profile?.goals?.primary_goal || 'overall_health',
    weekly_workout_target: user?.profile?.goals?.weekly_workout_target || 3,
    daily_water_target_ml: user?.profile?.goals?.daily_water_target_ml || 2000
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      toast({
        title: language === 'en' ? 'Saved!' : '¡Guardado!',
        description: language === 'en' ? 'Profile updated successfully' : 'Perfil actualizado correctamente'
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMetrics = async () => {
    setSaving(true);
    try {
      await updateMetrics({
        ...metrics,
        height_cm: metrics.height_cm ? parseFloat(metrics.height_cm) : null,
        weight_kg: metrics.weight_kg ? parseFloat(metrics.weight_kg) : null,
        target_weight_kg: metrics.target_weight_kg ? parseFloat(metrics.target_weight_kg) : null
      });
      toast({
        title: language === 'en' ? 'Saved!' : '¡Guardado!',
        description: language === 'en' ? 'Metrics updated successfully' : 'Métricas actualizadas correctamente'
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoals = async () => {
    setSaving(true);
    try {
      await updateGoals(goals);
      toast({
        title: language === 'en' ? 'Saved!' : '¡Guardado!',
        description: language === 'en' ? 'Goals updated successfully' : 'Objetivos actualizados correctamente'
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', icon: User, label: language === 'en' ? 'Profile' : 'Perfil' },
    { id: 'metrics', icon: Scale, label: language === 'en' ? 'Metrics' : 'Métricas' },
    { id: 'goals', icon: Target, label: language === 'en' ? 'Goals' : 'Objetivos' },
  ];

  const activityLevels = [
    { value: 'sedentary', label: language === 'en' ? 'Sedentary' : 'Sedentaria' },
    { value: 'light', label: language === 'en' ? 'Light' : 'Ligera' },
    { value: 'moderate', label: language === 'en' ? 'Moderate' : 'Moderada' },
    { value: 'active', label: language === 'en' ? 'Active' : 'Activa' },
    { value: 'very_active', label: language === 'en' ? 'Very Active' : 'Muy Activa' },
  ];

  const primaryGoals = [
    { value: 'lose_weight', label: language === 'en' ? 'Lose Weight' : 'Perder Peso' },
    { value: 'gain_muscle', label: language === 'en' ? 'Gain Muscle' : 'Ganar Músculo' },
    { value: 'improve_fitness', label: language === 'en' ? 'Improve Fitness' : 'Mejorar Condición' },
    { value: 'mental_wellness', label: language === 'en' ? 'Mental Wellness' : 'Bienestar Mental' },
    { value: 'overall_health', label: language === 'en' ? 'Overall Health' : 'Salud General' },
  ];

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {language === 'en' ? 'Profile Settings' : 'Configuración de Perfil'}
            </h1>
            <p className="text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
            <h2 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Personal Information' : 'Información Personal'}
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {language === 'en' ? 'First Name' : 'Nombre'}
                  </label>
                  <Input
                    value={profile.first_name}
                    onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                    className="bg-gray-800/50 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {language === 'en' ? 'Last Name' : 'Apellido'}
                  </label>
                  <Input
                    value={profile.last_name}
                    onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                    className="bg-gray-800/50 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Gender' : 'Género'}
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'female', label: language === 'en' ? 'Female' : 'Femenino' },
                    { value: 'male', label: language === 'en' ? 'Male' : 'Masculino' },
                    { value: 'other', label: language === 'en' ? 'Other' : 'Otro' },
                  ].map((g) => (
                    <button
                      key={g.value}
                      onClick={() => setProfile({...profile, gender: g.value})}
                      className={`flex-1 py-3 rounded-xl transition-all ${
                        profile.gender === g.value
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-800/50 text-gray-400 hover:text-white border border-gray-700'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full bg-emerald-500 hover:bg-emerald-600 mt-4"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Save Changes' : 'Guardar Cambios'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
            <h2 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Physical Metrics' : 'Métricas Físicas'}
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {language === 'en' ? 'Height (cm)' : 'Altura (cm)'}
                  </label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="number"
                      value={metrics.height_cm}
                      onChange={(e) => setMetrics({...metrics, height_cm: e.target.value})}
                      className="pl-10 bg-gray-800/50 border-gray-700 text-white"
                      placeholder="170"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {language === 'en' ? 'Weight (kg)' : 'Peso (kg)'}
                  </label>
                  <div className="relative">
                    <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="number"
                      value={metrics.weight_kg}
                      onChange={(e) => setMetrics({...metrics, weight_kg: e.target.value})}
                      className="pl-10 bg-gray-800/50 border-gray-700 text-white"
                      placeholder="65"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Target Weight (kg)' : 'Peso Objetivo (kg)'}
                </label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="number"
                    value={metrics.target_weight_kg}
                    onChange={(e) => setMetrics({...metrics, target_weight_kg: e.target.value})}
                    className="pl-10 bg-gray-800/50 border-gray-700 text-white"
                    placeholder="60"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Activity Level' : 'Nivel de Actividad'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {activityLevels.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setMetrics({...metrics, activity_level: level.value})}
                      className={`py-3 px-2 rounded-xl text-sm transition-all ${
                        metrics.activity_level === level.value
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-800/50 text-gray-400 hover:text-white border border-gray-700'
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleSaveMetrics}
                disabled={saving}
                className="w-full bg-emerald-500 hover:bg-emerald-600 mt-4"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Save Metrics' : 'Guardar Métricas'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
            <h2 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Fitness Goals' : 'Objetivos de Fitness'}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Primary Goal' : 'Objetivo Principal'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {primaryGoals.map((goal) => (
                    <button
                      key={goal.value}
                      onClick={() => setGoals({...goals, primary_goal: goal.value})}
                      className={`py-3 px-4 rounded-xl text-sm transition-all ${
                        goals.primary_goal === goal.value
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-800/50 text-gray-400 hover:text-white border border-gray-700'
                      }`}
                    >
                      {goal.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Weekly Workout Target' : 'Meta Semanal de Entrenamientos'}
                </label>
                <div className="flex items-center space-x-4">
                  <Dumbbell className="w-5 h-5 text-emerald-400" />
                  <input
                    type="range"
                    min="1"
                    max="7"
                    value={goals.weekly_workout_target}
                    onChange={(e) => setGoals({...goals, weekly_workout_target: parseInt(e.target.value)})}
                    className="flex-1 h-2 bg-gray-700 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  />
                  <span className="text-white font-bold text-xl w-8 text-center">
                    {goals.weekly_workout_target}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {language === 'en' ? 'Daily Water Target (ml)' : 'Meta Diaria de Agua (ml)'}
                </label>
                <div className="flex items-center space-x-4">
                  <Apple className="w-5 h-5 text-blue-400" />
                  <input
                    type="range"
                    min="1000"
                    max="4000"
                    step="250"
                    value={goals.daily_water_target_ml}
                    onChange={(e) => setGoals({...goals, daily_water_target_ml: parseInt(e.target.value)})}
                    className="flex-1 h-2 bg-gray-700 rounded-full appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-white font-bold text-xl w-16 text-center">
                    {goals.daily_water_target_ml}ml
                  </span>
                </div>
              </div>

              <Button
                onClick={handleSaveGoals}
                disabled={saving}
                className="w-full bg-emerald-500 hover:bg-emerald-600 mt-4"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Save Goals' : 'Guardar Objetivos'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
